import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Typography,
  Divider,
  Switch,
  FormControlLabel,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  Home as HomeIcon,
  Person as PersonIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { GlassmorphicCard } from '../styles/sharedStyles';

const IPBlocking = ({ apiBaseUrl, token }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeTab, setActiveTab] = useState(0);
  
  // Blocked IPs state
  const [ipBlocks, setIpBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [blockingEnabled, setBlockingEnabled] = useState(true);
  const [newIP, setNewIP] = useState('');

  // Home IP Ranges state
  const [homeIPRanges, setHomeIPRanges] = useState(['192.168.0.', '192.168.8.']);
  const [newHomeRange, setNewHomeRange] = useState('');
  const [loadingRanges, setLoadingRanges] = useState(true);

  // Trusted IPs state
  const [trustedIPs, setTrustedIPs] = useState({});
  const [loadingTrusted, setLoadingTrusted] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Fetch IP blocks on mount
  useEffect(() => {
    fetchIPBlocks();
    fetchHomeIPRanges();
    fetchTrustedIPs();
  }, []);

  const fetchIPBlocks = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiBaseUrl}/api/ip-blocking`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIpBlocks(response.data.blocked_ips || []);
      setBlockingEnabled(response.data.enabled || true);
      setErrorMessage('');
    } catch (err) {
      console.error('Error fetching IP blocks:', err);
      setErrorMessage('Failed to load IP blocks');
    } finally {
      setLoading(false);
    }
  };

  const fetchHomeIPRanges = async () => {
    try {
      setLoadingRanges(true);
      const response = await axios.get(`${apiBaseUrl}/api/ip-blocking/home-ranges`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setHomeIPRanges(response.data.home_ip_ranges || ['192.168.0.', '192.168.8.']);
      }
    } catch (err) {
      console.error('Error fetching home IP ranges:', err);
    } finally {
      setLoadingRanges(false);
    }
  };

  const fetchTrustedIPs = async () => {
    try {
      setLoadingTrusted(true);
      const response = await axios.get(`${apiBaseUrl}/api/ip-blocking/trusted-ips`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setTrustedIPs(response.data.trusted_ips || {});
      }
    } catch (err) {
      console.error('Error fetching trusted IPs:', err);
    } finally {
      setLoadingTrusted(false);
    }
  };

  const handleAddIP = async () => {
    if (!newIP.trim()) {
      setErrorMessage('Please enter an IP address');
      return;
    }

    // Simple IP validation
    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
    if (!ipRegex.test(newIP.trim())) {
      setErrorMessage('Invalid IP address format');
      return;
    }

    try {
      setSaving(true);
      setErrorMessage('');
      setSuccessMessage('');

      const response = await axios.post(
        `${apiBaseUrl}/api/ip-blocking`,
        { ip_address: newIP.trim() },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setSuccessMessage(`✅ IP ${newIP} blocked successfully`);
        setNewIP('');
        await fetchIPBlocks();
      } else {
        setErrorMessage(response.data.message || 'Failed to block IP');
      }
    } catch (err) {
      console.error('Error blocking IP:', err);
      setErrorMessage(err.response?.data?.message || 'Error blocking IP');
    } finally {
      setSaving(false);
    }
  };

  const handleUnblockIP = async (ip) => {
    try {
      setSaving(true);
      const response = await axios.delete(
        `${apiBaseUrl}/api/ip-blocking/${ip}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setSuccessMessage(`✅ IP ${ip} unblocked`);
        await fetchIPBlocks();
      } else {
        setErrorMessage(response.data.message || 'Failed to unblock IP');
      }
    } catch (err) {
      console.error('Error unblocking IP:', err);
      setErrorMessage(err.response?.data?.message || 'Error unblocking IP');
    } finally {
      setSaving(false);
    }
  };

  const handleWipeUserTrustedIPs = async (username) => {
    try {
      setSaving(true);
      const response = await axios.delete(
        `${apiBaseUrl}/api/ip-blocking/trusted-ips/${username}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setSuccessMessage(response.data.message || `✅ Trusted IPs cleared for ${username}`);
        await fetchTrustedIPs();
      } else {
        setErrorMessage(response.data.message || 'Failed to wipe trusted IPs');
      }
    } catch (err) {
      console.error('Error wiping trusted IPs:', err);
      setErrorMessage(err.response?.data?.message || 'Error wiping trusted IPs');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleBlocking = async () => {
    try {
      setSaving(true);
      const response = await axios.post(
        `${apiBaseUrl}/api/ip-blocking/toggle`,
        { enabled: !blockingEnabled },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setBlockingEnabled(!blockingEnabled);
        setSuccessMessage(
          `✅ IP blocking ${!blockingEnabled ? 'enabled' : 'disabled'}`
        );
      }
    } catch (err) {
      console.error('Error toggling blocking:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleAddHomeRange = async () => {
    if (!newHomeRange.trim()) {
      setErrorMessage('Please enter a subnet prefix');
      return;
    }

    // Validate subnet format (must end with .)
    if (!newHomeRange.trim().endsWith('.')) {
      setErrorMessage('Subnet must end with "." (e.g., 192.168.0.)');
      return;
    }

    // Basic validation
    const parts = newHomeRange.trim().replace(/\.+$/, '').split('.');
    if (parts.length !== 3) {
      setErrorMessage('Invalid subnet format. Use format: xxx.xxx.xxx. (e.g., 192.168.0.)');
      return;
    }

    for (const part of parts) {
      if (!/^\d+$/.test(part) || parseInt(part) > 255) {
        setErrorMessage('Invalid IP numbers in subnet');
        return;
      }
    }

    try {
      setSaving(true);
      setErrorMessage('');
      setSuccessMessage('');

      const newRanges = [...homeIPRanges, newHomeRange.trim()];
      const response = await axios.post(
        `${apiBaseUrl}/api/ip-blocking/home-ranges`,
        { home_ip_ranges: newRanges },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setSuccessMessage(`✅ Home IP range added successfully`);
        setNewHomeRange('');
        setHomeIPRanges(newRanges);
      } else {
        setErrorMessage(response.data.message || 'Failed to add home IP range');
      }
    } catch (err) {
      console.error('Error adding home IP range:', err);
      setErrorMessage(err.response?.data?.message || 'Error adding home IP range');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveHomeRange = async (range) => {
    try {
      setSaving(true);
      setErrorMessage('');
      setSuccessMessage('');

      const newRanges = homeIPRanges.filter(r => r !== range);
      const response = await axios.post(
        `${apiBaseUrl}/api/ip-blocking/home-ranges`,
        { home_ip_ranges: newRanges },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setSuccessMessage(`✅ Home IP range removed successfully`);
        setHomeIPRanges(newRanges);
      } else {
        setErrorMessage(response.data.message || 'Failed to remove home IP range');
      }
    } catch (err) {
      console.error('Error removing home IP range:', err);
      setErrorMessage(err.response?.data?.message || 'Error removing home IP range');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress sx={{ color: '#00d4ff' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '900px', mx: 'auto' }}>
      {/* Messages */}
      {successMessage && (
        <Alert
          severity="success"
          onClose={() => setSuccessMessage('')}
          sx={{ mb: 2, borderRadius: '12px' }}
        >
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert
          severity="error"
          onClose={() => setErrorMessage('')}
          sx={{ mb: 2, borderRadius: '12px' }}
        >
          {errorMessage}
        </Alert>
      )}

      {/* Tab Navigation */}
      <Box
        sx={{
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          background: 'rgba(255, 255, 255, 0.02)',
          borderRadius: '16px 16px 0 0',
          mb: 0,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: '48px',
            '& .MuiTabs-indicator': {
              background: '#00d4ff',
              height: '3px',
            },
            '& .MuiTab-root': {
              color: 'rgba(255, 255, 255, 0.6)',
              textTransform: 'none',
              fontSize: '0.85rem',
              fontWeight: 500,
              py: 1.5,
              px: 2,
              minWidth: 'auto',
              '&:hover': {
                color: '#ffffff',
                background: 'rgba(255, 255, 255, 0.05)',
              },
              '&.Mui-selected': {
                color: '#00d4ff',
                background: alpha('#00d4ff', 0.05),
              },
            },
          }}
        >
          <Tab icon={<BlockIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Blocked IPs" />
          <Tab icon={<HomeIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Home IP Ranges" />
          <Tab icon={<PersonIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Trusted IPs" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <GlassmorphicCard sx={{ borderRadius: '0 0 16px 16px' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          
          {/* Tab 0: Blocked IPs */}
          {activeTab === 0 && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#00d4ff',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  🚫 IP Blocking
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={blockingEnabled}
                      onChange={handleToggleBlocking}
                      disabled={saving}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#00d4ff',
                          '& + .MuiSwitch-track': {
                            backgroundColor: 'rgba(0, 212, 255, 0.3)',
                          },
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ color: blockingEnabled ? '#00ff88' : 'rgba(255, 255, 255, 0.7)', ml: 1 }}>
                      {blockingEnabled ? 'Enabled' : 'Disabled'}
                    </Typography>
                  }
                />
              </Box>

              <Typography
                variant="body2"
                sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6 }}
              >
                Block specific IP addresses from accessing the application. IPs are automatically blocked permanently after 5 failed login attempts, or after excessive requests (e.g. bot scanning). Home IPs are never auto-blocked.
              </Typography>

              <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

              {/* Add IP Section */}
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 2,
                  color: '#00d4ff',
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  fontSize: '0.85rem',
                  letterSpacing: '0.5px',
                }}
              >
                Block New IP
              </Typography>

              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                mb: 3,
                flexDirection: isMobile ? 'column' : 'row'
              }}>
                <TextField
                  fullWidth
                  label="IP Address"
                  value={newIP}
                  onChange={(e) => setNewIP(e.target.value)}
                  placeholder="e.g., 192.168.1.100"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddIP()}
                  size="small"
                  disabled={!blockingEnabled}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#fff',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&.Mui-focused fieldset': { borderColor: '#00d4ff' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleAddIP}
                  disabled={saving || !blockingEnabled || !newIP.trim()}
                  sx={{
                    backgroundColor: (theme) => theme.palette.secondary.main,
                    color: (theme) => theme.palette.secondary.contrastText,
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '8px',
                    px: 3,
                    minWidth: isMobile ? '100%' : 'auto',
                    '&:hover': {
                      backgroundColor: (theme) => theme.palette.secondary.main,
                      filter: 'brightness(1.1)',
                      boxShadow: (theme) => `0 0 20px ${theme.palette.secondary.main}66`,
                    },
                    '&:disabled': { opacity: 0.6 },
                  }}
                >
                  <AddIcon sx={{ mr: 1 }} />
                  Block
                </Button>
              </Box>

              <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

              {/* Blocked IPs List */}
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 2,
                  color: '#00d4ff',
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  fontSize: '0.85rem',
                  letterSpacing: '0.5px',
                }}
              >
                Blocked IP Addresses ({ipBlocks.length})
              </Typography>

              {ipBlocks.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {ipBlocks.map((block) => (
                    <Card
                      key={block.ip}
                      sx={{
                        background: 'rgba(255, 255, 255, 0.02)',
                        borderRadius: '12px',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 212, 255, 0.05)',
                          borderColor: 'rgba(0, 212, 255, 0.3)',
                        }
                      }}
                    >
                      <CardContent sx={{ 
                        py: 2, 
                        px: isMobile ? 2 : 3,
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: isMobile ? 2 : 0
                      }}>
                        <Box sx={{ flex: 1, width: isMobile ? '100%' : 'auto' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: isMobile ? 1 : 0 }}>
                            <BlockIcon sx={{ color: '#ff6b6b', fontSize: 20 }} />
                            <Typography 
                              sx={{ 
                                color: '#fff', 
                                fontFamily: 'monospace',
                                fontWeight: 500,
                                fontSize: isMobile ? '1rem' : '0.95rem'
                              }}
                            >
                              {block.ip}
                            </Typography>
                          </Box>
                          <Typography 
                            sx={{ 
                              color: 'rgba(255, 255, 255, 0.5)', 
                              fontSize: isMobile ? '0.75rem' : '0.8rem',
                              ml: isMobile ? 0 : 3.5
                            }}
                          >
                            Blocked: {new Date(block.blocked_at * 1000).toLocaleString()}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          onClick={() => handleUnblockIP(block.ip)}
                          disabled={saving}
                          variant="outlined"
                          sx={{
                            color: '#ff6b6b',
                            borderColor: 'rgba(255, 107, 107, 0.5)',
                            minWidth: isMobile ? '100%' : 'auto',
                            '&:hover': { 
                              backgroundColor: 'rgba(255, 107, 107, 0.1)',
                              borderColor: '#ff6b6b'
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                          Unblock
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              ) : (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 6,
                    color: 'rgba(255, 255, 255, 0.7)',
                    background: 'rgba(255, 255, 255, 0.02)',
                    borderRadius: '12px',
                    border: '1px dashed rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <BlockIcon sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
                  <Typography variant="body1">No blocked IPs</Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                    IPs will appear here after failed login attempts
                  </Typography>
                </Box>
              )}
            </>
          )}

          {/* Tab 1: Home IP Ranges */}
          {activeTab === 1 && (
            <>
              <Typography
                variant="h6"
                sx={{
                  color: '#00d4ff',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 2,
                }}
              >
                🏠 Home IP Ranges
              </Typography>

              <Typography
                variant="body2"
                sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6 }}
              >
                These IP subnets are never blocked by auto-blocking (failed logins or rate limiting). Add your home network ranges here.
              </Typography>

              <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

              {/* Add Home Range Section */}
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 2,
                  color: '#00d4ff',
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  fontSize: '0.85rem',
                  letterSpacing: '0.5px',
                }}
              >
                Add Home IP Range
              </Typography>

              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                mb: 3,
                flexDirection: isMobile ? 'column' : 'row'
              }}>
                <TextField
                  fullWidth
                  label="Subnet Prefix"
                  value={newHomeRange}
                  onChange={(e) => setNewHomeRange(e.target.value)}
                  placeholder="e.g., 192.168.0."
                  onKeyPress={(e) => e.key === 'Enter' && handleAddHomeRange()}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#fff',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&.Mui-focused fieldset': { borderColor: '#00d4ff' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleAddHomeRange}
                  disabled={saving || !newHomeRange.trim()}
                  sx={{
                    backgroundColor: '#00d4ff',
                    color: '#000',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '8px',
                    px: 3,
                    minWidth: isMobile ? '100%' : 'auto',
                    '&:hover': {
                      backgroundColor: '#00d4ff',
                      filter: 'brightness(1.1)',
                      boxShadow: '0 0 20px rgba(0, 212, 255, 0.4)',
                    },
                    '&:disabled': { opacity: 0.6 },
                  }}
                >
                  <AddIcon sx={{ mr: 1 }} />
                  Add Range
                </Button>
              </Box>

              <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

              {/* Home IP Ranges List */}
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 2,
                  color: '#00d4ff',
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  fontSize: '0.85rem',
                  letterSpacing: '0.5px',
                }}
              >
                Home IP Subnets ({homeIPRanges.length})
              </Typography>

              {loadingRanges ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress sx={{ color: '#00d4ff' }} />
                </Box>
              ) : homeIPRanges.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {homeIPRanges.map((range) => (
                    <Card
                      key={range}
                      sx={{
                        background: 'rgba(255, 255, 255, 0.02)',
                        borderRadius: '12px',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 212, 255, 0.05)',
                          borderColor: 'rgba(0, 212, 255, 0.3)',
                        }
                      }}
                    >
                      <CardContent sx={{ 
                        py: 2, 
                        px: isMobile ? 2 : 3,
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: isMobile ? 2 : 0
                      }}>
                        <Box sx={{ flex: 1, width: isMobile ? '100%' : 'auto' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: isMobile ? 1 : 0 }}>
                            <HomeIcon sx={{ color: '#00ff88', fontSize: 20 }} />
                            <Typography 
                              sx={{ 
                                color: '#fff', 
                                fontFamily: 'monospace',
                                fontWeight: 500,
                                fontSize: isMobile ? '1rem' : '0.95rem'
                              }}
                            >
                              {range}x
                            </Typography>
                          </Box>
                          <Typography 
                            sx={{ 
                              color: 'rgba(255, 255, 255, 0.5)', 
                              fontSize: isMobile ? '0.75rem' : '0.8rem',
                              ml: isMobile ? 0 : 3.5
                            }}
                          >
                            Matches: {range}0 - {range}255
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          onClick={() => handleRemoveHomeRange(range)}
                          disabled={saving}
                          variant="outlined"
                          sx={{
                            color: '#ff6b6b',
                            borderColor: 'rgba(255, 107, 107, 0.5)',
                            minWidth: isMobile ? '100%' : 'auto',
                            '&:hover': { 
                              backgroundColor: 'rgba(255, 107, 107, 0.1)',
                              borderColor: '#ff6b6b'
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                          Remove
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              ) : (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 6,
                    color: 'rgba(255, 255, 255, 0.7)',
                    background: 'rgba(255, 255, 255, 0.02)',
                    borderRadius: '12px',
                    border: '1px dashed rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <HomeIcon sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
                  <Typography variant="body1">No home IP ranges configured</Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                    Add your home network subnets above
                  </Typography>
                </Box>
              )}
            </>
          )}

          {/* Tab 2: Trusted IPs (Read Only) */}
          {activeTab === 2 && (
            <>
               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                 <Typography
                   variant="h6"
                   sx={{
                     color: '#00d4ff',
                     fontWeight: 600,
                     display: 'flex',
                     alignItems: 'center',
                     gap: 1,
                   }}
                 >
                   👤 User-Specific Trusted IPs
                 </Typography>
                 {Object.keys(trustedIPs).length > 0 && (
                   <Button
                     size="small"
                     variant="outlined"
                     onClick={() => {
                       const user = Object.keys(trustedIPs)[0];
                       setUserToDelete(user);
                       setDeleteDialogOpen(true);
                   }}
                     disabled={saving}
                     sx={{
                       color: '#ff6b6b',
                       borderColor: 'rgba(255, 107, 107, 0.5)',
                       '&:hover': {
                         backgroundColor: 'rgba(255, 107, 107, 0.1)',
                         borderColor: '#ff6b6b',
                       },
                     }}
                   >
                     Wipe
                   </Button>
                 )}
               </Box>

              <Typography
                variant="body2"
                sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6 }}
              >
                These are user-specific trusted IPs stored in the system. Users with trusted IPs don't receive 2FA challenges. This view is read-only.
              </Typography>

              <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

              {loadingTrusted ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress sx={{ color: '#00d4ff' }} />
                </Box>
              ) : Object.keys(trustedIPs).length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {Object.entries(trustedIPs).map(([username, ips]) => (
                    <Card
                      key={username}
                      sx={{
                        background: 'rgba(255, 255, 255, 0.02)',
                        borderRadius: '12px',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                      }}
                    >
                      <CardContent sx={{ py: 2, px: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon sx={{ color: '#00d4ff', fontSize: 20 }} />
                            <Typography 
                              sx={{ 
                                color: '#fff', 
                                fontWeight: 600,
                                fontSize: '1rem'
                              }}
                            >
                              {username}
                            </Typography>
                            <Typography 
                              sx={{ 
                                color: 'rgba(255, 255, 255, 0.5)', 
                                fontSize: '0.8rem',
                                ml: 1
                              }}
                            >
                              ({Array.isArray(ips) ? ips.length : 0} IPs)
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setUserToDelete(username);
                              setDeleteDialogOpen(true);
                            }}
                            disabled={saving}
                            sx={{
                              color: '#ff6b6b',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 107, 107, 0.1)',
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {Array.isArray(ips) && ips.map((ip, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                background: 'rgba(0, 212, 255, 0.1)',
                                border: '1px solid rgba(0, 212, 255, 0.3)',
                                borderRadius: '6px',
                                px: 1.5,
                                py: 0.5,
                                fontFamily: 'monospace',
                                fontSize: '0.85rem',
                                color: '#fff',
                              }}
                            >
                              {ip}
                            </Box>
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              ) : (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 6,
                    color: 'rgba(255, 255, 255, 0.7)',
                    background: 'rgba(255, 255, 255, 0.02)',
                    borderRadius: '12px',
                    border: '1px dashed rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <PersonIcon sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
                  <Typography variant="body1">No trusted IPs configured</Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                    Trusted IPs are added when users log in from new locations
                  </Typography>
                </Box>
              )}
            </>
          )}

         </CardContent>
       </GlassmorphicCard>

       {/* Confirmation Dialog */}
       <Dialog
         open={deleteDialogOpen}
         onClose={() => setDeleteDialogOpen(false)}
         PaperProps={{
           sx: {
             background: 'rgba(15, 15, 25, 0.95)',
             backdropFilter: 'blur(20px)',
             border: '1px solid rgba(255, 255, 255, 0.1)',
             borderRadius: '16px',
           },
         }}
       >
         <DialogTitle sx={{ color: '#ff6b6b', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
           <DeleteIcon sx={{ fontSize: 24 }} />
           Confirm Wipe
         </DialogTitle>
         <DialogContent>
           <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 1 }}>
             Are you sure you want to wipe all trusted IPs for user <strong style={{color: '#fff'}}>"{userToDelete}"</strong>?
           </Typography>
           <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.8rem', mt: 1 }}>
             This user will no longer bypass 2FA from previously trusted locations.
           </Typography>
         </DialogContent>
         <DialogActions sx={{ p: 2 }}>
           <Button
             onClick={() => setDeleteDialogOpen(false)}
             disabled={saving}
             sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
           >
             Cancel
           </Button>
           <Button
             onClick={async () => {
               await handleWipeUserTrustedIPs(userToDelete);
               setDeleteDialogOpen(false);
             }}
             disabled={saving}
             sx={{
               backgroundColor: '#ff6b6b',
               color: '#fff',
               '&:hover': { opacity: 0.8 },
             }}
             variant="contained"
           >
             Wipe Trusted IPs
           </Button>
         </DialogActions>
       </Dialog>
     </Box>
   );
};

export default IPBlocking;
